// LiveKit compatibility shim — replaces the deleted public/EnxRtc.js global.
//
// The 3 interview screens (rec-round, l1-round, device-checking) call:
//   window.EnxRtc.getDevices(cb)
//   window.EnxRtc.joinRoom(token, config, cb)            → returns { room, streams, publishId }
//   window.EnxRtc.clientDiagnostics(opts, cb)            → device-check
//   room.subscribe(stream)                               → no-op (LiveKit auto-subscribes)
//   room.addEventListener(name, handler)                 → mapped to LiveKit RoomEvent
//   room.startLiveRecording(cfg, cb)                     → POST backend /api/livekit/recordings/start
//   room.stopLiveRecording(cb)                           → POST backend /api/livekit/recordings/stop
//   streamEvent.stream.getID()                           → participant identity
//
// Imported once from src/index.js so it registers window.EnxRtc at app boot.
// Not a complete EnxRtc port — covers the API surface the 3 screens actually use.

import { Room, RoomEvent, createLocalTracks } from 'livekit-client';

const EVENT_MAP = {
  'room-connected':              RoomEvent.Connected,
  'room-disconnected':           RoomEvent.Disconnected,
  'room-error':                  RoomEvent.MediaDevicesError,
  'user-connected':              RoomEvent.ParticipantConnected,
  'user-disconnected':           RoomEvent.ParticipantDisconnected,
  'stream-subscribed':           RoomEvent.TrackSubscribed,
  'network-disconnected':        RoomEvent.Reconnecting,
  'network-reconnected':         RoomEvent.Reconnected,
  'network-reconnect-timeout':   RoomEvent.SignalReconnecting,
  'network-reconnect-failed':    RoomEvent.SignalConnected,
  // Live recording events fire from server-side webhook → frontend can't observe directly.
  // We swallow these registrations; recording state is tracked server-side.
  'room-live-recording-on':      null,
  'room-live-recording-off':     null,
  'room-live-recording-failed':  null,
};

// Map a LiveKit Track / Participant event payload back to the EnxStream-shaped object
// that the existing UI expects. The UI only calls .getID(), so that's all we expose.
function wrapStream(participant, publication, track) {
  return {
    getID: () => participant?.identity || publication?.trackSid || 'unknown',
    _participant: participant,
    _publication: publication,
    _track: track,
  };
}

// Wrap a livekit-client Room so the existing UI's `room.addEventListener(...)` and
// `room.subscribe(stream)` calls continue to work.
function wrapRoom(lkRoom) {
  return {
    _lkRoom: lkRoom,

    // EnableX exposed an explicit subscribe(stream) call. LiveKit auto-subscribes by default,
    // so this is a no-op compatibility shim.
    subscribe: () => {},

    // Map EnxRtc event-name strings to LiveKit RoomEvent enums.
    addEventListener: (eventName, handler) => {
      const lkEvent = EVENT_MAP[eventName];
      if (lkEvent === null) return;            // intentional swallow (recording-* events)
      if (lkEvent === undefined) {
        console.warn('[livekit-shim] unmapped event:', eventName);
        return;
      }
      if (eventName === 'stream-subscribed') {
        lkRoom.on(lkEvent, (track, publication, participant) => {
          handler({ stream: wrapStream(participant, publication, track) });
        });
      } else {
        lkRoom.on(lkEvent, (...args) => handler(...args));
      }
    },

    // Recording start/stop are now server-driven via backend endpoints.
    startLiveRecording: (cfg, cb) => {
      const url = `${getApiBase()}/job-posting/api/livekit/recordings/start`;
      fetch(url, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({ roomName: lkRoom.name, config: cfg }),
      })
        .then(r => r.json())
        .then(j => cb && cb({ result: 0, egressId: j.egressId }))
        .catch(e => cb && cb({ result: 1, error: e.message }));
    },
    stopLiveRecording: (cb) => {
      const url = `${getApiBase()}/job-posting/api/livekit/recordings/stop`;
      fetch(url, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({ roomName: lkRoom.name }),
      })
        .then(r => r.json())
        .then(() => cb && cb({ result: 0 }))
        .catch(e => cb && cb({ result: 1, error: e.message }));
    },

    disconnect: () => lkRoom.disconnect(),
  };
}

function getApiBase() {
  // best-effort match to how the rest of the app builds API URLs
  if (typeof window !== 'undefined' && window.__AMPLE_API_BASE) return window.__AMPLE_API_BASE;
  return '';
}
function jsonHeaders() {
  const token = localStorage.getItem('e_access_token');
  const h = { 'Content-Type': 'application/json' };
  if (token) h['Authorization'] = `Bearer ${token}`;
  return h;
}

const EnxRtcShim = {
  // List devices in EnableX-shaped { result, devices: { cam: [...], mic: [...] } } envelope.
  getDevices: async (cb) => {
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      const cam = all.filter(d => d.kind === 'videoinput').map((d, i) => ({
        deviceId: d.deviceId, label: d.label, default: i === 0,
      }));
      const mic = all.filter(d => d.kind === 'audioinput').map((d, i) => ({
        deviceId: d.deviceId, label: d.label, default: i === 0,
      }));
      cb({ result: 0, devices: { cam, mic } });
    } catch (e) {
      cb({ result: 1, error: e.message, devices: { cam: [], mic: [] } });
    }
  },

  // joinRoom(token, config, cb). config = { video, audio, data, videoSize }.
  // The candidate's join flow gets `url` + `token` from POST /api/livekit/create-room-and-generate-token.
  // EnableX took only the token and inferred the URL; LiveKit needs both. We read url from
  // window.__LIVEKIT_URL (set by the screen right before calling joinRoom), or from the token's
  // `iss` claim as a fallback hint. If neither is set, the shim refuses politely.
  joinRoom: async (token, config, cb) => {
    const url = (typeof window !== 'undefined' && window.__LIVEKIT_URL) || null;
    if (!url) {
      const err = new Error('LIVEKIT_URL not set; the screen should set window.__LIVEKIT_URL from the create-room API response.');
      console.error(err);
      cb && cb(null, err);
      return null;
    }

    try {
      const lkRoom = new Room();

      // Connect (this triggers token verification + signaling)
      await lkRoom.connect(url, token);

      // Publish local tracks based on config.video / config.audio (EnableX-style).
      const tracks = await createLocalTracks({
        audio: !!config?.audio,
        video: !!config?.video,
      });
      for (const t of tracks) {
        await lkRoom.localParticipant.publishTrack(t);
      }

      const wrapped = wrapRoom(lkRoom);
      const result = {
        room: wrapped,
        publishId: lkRoom.localParticipant?.identity || 'self',
        streams: [], // Auto-subscription handles remote streams via TrackSubscribed events.
      };
      cb && cb(result, null);
      return result;
    } catch (e) {
      console.error('[livekit-shim] joinRoom failed', e);
      cb && cb(null, e);
      return null;
    }
  },

  // Hardware diagnostics. Returns one entry per option, EnableX-shaped.
  clientDiagnostics: async (options, cb) => {
    for (const opt of options || []) {
      let entry = { result: false, supported: false };
      try {
        if (opt === 'mic' || opt === 'audio') {
          const s = await navigator.mediaDevices.getUserMedia({ audio: true });
          s.getTracks().forEach(t => t.stop());
          entry = { result: true, supported: true };
        } else if (opt === 'cam' || opt === 'camera' || opt === 'video') {
          const s = await navigator.mediaDevices.getUserMedia({ video: true });
          s.getTracks().forEach(t => t.stop());
          entry = { result: true, supported: true };
        } else if (opt === 'network' || opt === 'connection') {
          entry = { result: navigator.onLine, supported: true };
        } else {
          entry = { result: true, supported: true };
        }
      } catch (e) {
        entry = { result: false, supported: false, error: e.message };
      }
      cb && cb({ [opt]: entry });
    }
  },
};

if (typeof window !== 'undefined') {
  window.EnxRtc = EnxRtcShim;
  console.log('[livekit-shim] window.EnxRtc registered (livekit-client backed)');
}

export default EnxRtcShim;
